import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { ECO, ecosystemPhases, renderEcosystem } from '../../../components/ecosystemMap';
import { draw, sampler } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';

const CORE_ORIGIN = `${ECO.cx} ${ECO.cy}`;
const VIEW_NEAR = '0 0 1600 900';
const VIEW_FAR = '-420 -250 2440 1372';

/** Unnamed future nodes, spread around the outside so none reads as a sequel to one product. */
const GHOSTS = [-168, -122, -60, -18, 16, 62, 118, 164].map((deg, i) => {
  const a = (deg * Math.PI) / 180;
  const reach = 1.55 + (i % 3) * 0.12;
  return {
    x: ECO.cx + Math.cos(a) * ECO.rx * reach,
    y: ECO.cy + Math.sin(a) * ECO.ry * reach * 1.25,
    fromX: ECO.cx + Math.cos(a) * ECO.rx,
    fromY: ECO.cy + Math.sin(a) * ECO.ry,
    r: 34 + (i % 4) * 8,
  };
});

/**
 * 0 The ecosystem comes back together: MINT and its branches, then the other products, then selected work
 * 1 Every connection returns toward Moderta
 * 2 The view pulls back; there is room around it — A GROWING ECOSYSTEM.
 * 3 BUILT AROUND PROBLEMS WORTH SOLVING.
 */
export class FutureScene extends BaseScene {
  public readonly stepCount = 4;

  private spin?: gsap.core.Tween;
  private returning = false;
  private returnTweens: gsap.core.Tween[] = [];

  protected template(): string {
    const ghosts = GHOSTS.map((g) => `
      <g class="fu-ghost" data-from="2">
        <path class="fu-ghost__path draw" pathLength="1" d="M${g.fromX.toFixed(1)} ${g.fromY.toFixed(1)} L${g.x.toFixed(1)} ${g.y.toFixed(1)}"/>
        <circle class="fu-ghost__glow" cx="${g.x.toFixed(1)}" cy="${g.y.toFixed(1)}" r="${g.r}" fill="url(#fu-ghost)"/>
        <circle class="fu-ghost__ring" cx="${g.x.toFixed(1)}" cy="${g.y.toFixed(1)}" r="${(g.r * 0.45).toFixed(1)}"/>
      </g>`).join('');

    return `
      <div class="scene-layout future">
        <svg class="diagram fu-diagram" viewBox="${VIEW_NEAR}" role="img" aria-label="The Moderta ecosystem, connected back to Moderta, with room around it for what comes next">
          <defs>
            <radialGradient id="fu-ghost">
              <stop offset="0" stop-color="#ffffff" stop-opacity=".32"/>
              <stop offset=".45" stop-color="#67f7d2" stop-opacity=".12"/>
              <stop offset="1" stop-color="#67f7d2" stop-opacity="0"/>
            </radialGradient>
          </defs>
          <ellipse class="fu-outer" data-from="2" cx="${ECO.cx}" cy="${ECO.cy}" rx="${ECO.rx * 1.62}" ry="${ECO.ry * 1.9}"/>
          ${ghosts}
          ${renderEcosystem(this.context, () => 0)}
          <circle class="fu-core-glow" cx="${ECO.cx}" cy="${ECO.cy}" r="${ECO.coreRadius + 40}"/>
        </svg>
        <div class="statement-stack fu-statements">
          <h2 class="statement" data-from="2">${words('A GROWING ECOSYSTEM.')}</h2>
          <h2 class="statement statement--accent" data-from="3">${words('BUILT AROUND PROBLEMS WORTH SOLVING.')}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    this.ensureSpin();
    draw(this.$$('.eco-spoke, .motif'), true, { immediate: true });
    if (immediate || this.step !== 0) return;
    // The hierarchy is the reveal order: MINT with its branches first, selected work last and quietest.
    const tl = this.timeline();
    tl.fromTo(this.$('.eco-core'), { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0);
    ecosystemPhases(this.context).forEach((phase, p) => {
      phase.forEach((group, i) => {
        const at = 0.4 + p * 0.9 + i * 0.25;
        const spoke = this.$(`.eco-spoke[data-group="${group.id}"]`);
        draw(spoke, false, { immediate: true });
        tl.add(draw(spoke, true, { duration: 0.7 }), at);
        tl.fromTo(this.$(`.world[data-group="${group.id}"] .world__motion`), { opacity: 0, scale: 0.85, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.9, ease: 'expo.out' }, at + 0.3);
      });
    });
  }

  protected onExit(): void {
    this.spin = undefined;
    this.returning = false;
    this.returnTweens = [];
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('future', step, immediate);
    this.ensureSpin();

    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();
    draw(this.$$('.eco-spoke, .motif'), true, { immediate: true });
    gsap.set(this.$$('.world__motion'), { opacity: 1, scale: 1 });
    gsap.set(this.$('.eco-core'), { opacity: 1 });

    // 1 — everything flows back toward Moderta, and the core answers.
    gsap.to(this.$('.fu-core-glow'), { opacity: step >= 1 ? 1 : 0, scale: step >= 1 ? 1 : 0.6, svgOrigin: CORE_ORIGIN, duration: d * 1.2, delay: forward && step === 1 ? 0.8 : 0, ease: 'power2.out' });
    this.syncReturn(step >= 1, forward && step === 1);

    // 2 — pull back: the ecosystem gets smaller and the space around it becomes visible.
    const far = step >= 2;
    gsap.to(this.$('.fu-diagram'), { attr: { viewBox: far ? VIEW_FAR : VIEW_NEAR }, duration: d * 2.2, ease: 'power3.inOut' });
    const paths = this.$$('.fu-ghost__path');
    const glows = this.$$('.fu-ghost__glow, .fu-ghost__ring');
    if (forward && step === 2) {
      draw(paths, false, { immediate: true });
      tl.add(draw(paths, true, { duration: 1.2, stagger: 0.12, ease: 'power2.out' }), 1.2);
      tl.fromTo(glows, { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 1.4, stagger: 0.06, ease: 'power2.out' }, 1.6);
    } else {
      draw(paths, far, { immediate: true });
      gsap.set(glows, { opacity: 1, scale: 1, transformOrigin: '50% 50%' });
    }

    // 3 — today's orbit lights as the foundation for what comes next.
    const orbit = this.$('.eco-orbit--lit');
    if (forward && step === 3) tl.add(draw(orbit, true, { duration: 2, ease: 'power2.inOut' }), 0.4);
    else draw(orbit, step >= 3, { immediate: true });

    this.$$('.fu-statements .statement').forEach((s) => settleWords(s));
    if (forward && step >= 2) tl.add(revealWords(this.$$('.fu-statements .statement')[step - 2]), step === 2 ? 1.4 : 0.2);
  }

  /** Pulses run inward along every spoke: the products lead back to the same place. */
  private syncReturn(on: boolean, delayed: boolean): void {
    if (on === this.returning) return;
    this.returning = on;
    const markers = this.$$<SVGCircleElement>('.eco-pulse');
    this.returnTweens.forEach((t) => t.kill());
    this.returnTweens = [];
    markers.forEach((m) => { m.style.opacity = '0'; });
    if (!on) return;
    markers.forEach((marker, i) => {
      const spoke = this.$<SVGPathElement>(`.eco-spoke[data-group="${marker.dataset.group}"]`);
      const at = sampler(spoke);
      const proxy = { t: 1 };
      this.returnTweens.push(this.loop(gsap.to(proxy, {
        t: 0, duration: 1.6, delay: (delayed ? 0.3 : 0) + i * 0.3, repeat: -1, repeatDelay: 0.9, ease: 'power1.in',
        onUpdate: () => {
          const p = at(proxy.t);
          marker.setAttribute('transform', `translate(${p.x} ${p.y})`);
          marker.style.opacity = String(Math.sin(proxy.t * Math.PI));
        },
      })));
    });
  }

  private ensureSpin(): void {
    if (this.spin) return;
    this.spin = this.loop(gsap.to(this.$('.eco-core__spin'), { rotation: '+=360', svgOrigin: CORE_ORIGIN, duration: 70, ease: 'none', repeat: -1 }));
  }
}
